/**
 * FQN (Fully Qualified Name) 포맷팅 유틸리티
 */

/**
 * 경로 부분에 점(.), 공백, 특수문자가 포함되어 있으면 따옴표로 감싸기
 * 이미 따옴표로 감싸져 있으면 그대로 반환
 * @param part - 경로의 한 부분
 * @returns 필요시 따옴표로 감싸진 경로 부분
 *
 * @example
 * escapePathPart("lges.eswa.lam3") // returns: '"lges.eswa.lam3"'
 * escapePathPart("설비 계층 구조") // returns: '"설비 계층 구조"'
 * escapePathPart('"already.quoted"') // returns: '"already.quoted"'
 */
const escapePathPart = (part: string): string => {
  if (!part) return part;

  if (part.startsWith('"') && part.endsWith('"')) {
    return part;
  }

  const needsQuotes = part.includes('.') || part.includes(' ') || /[^\w-]/.test(part);

  if (needsQuotes) {
    return `"${part}"`;
  }

  return part;
};

/**
 * 경로 문자열을 FQN으로 변환
 * URL 인코딩된 문자(%20 등)를 디코딩하고, 필요한 부분에 따옴표를 추가
 *
 * @param path - "service / database / schema / table" 형식의 경로
 * @returns OpenMetadata FQN 형식 (점이나 공백이 포함된 부분은 따옴표로 감싸짐)
 *
 * @example
 * convertPathToFqn("ceph-s3 / lges.eswa.lam3")
 * // returns: 'ceph-s3."lges.eswa.lam3"'
 *
 * @example
 * convertPathToFqn("glossary / g.pdx-v2 / 설비%20계층%20구조 / Area")
 * // returns: 'glossary."g.pdx-v2"."설비 계층 구조".Area'
 *
 * @example
 * convertPathToFqn("mysql / production / public / users")
 * // returns: 'mysql.production.public.users'
 */
export const convertPathToFqn = (path: string): string => {
  try {
    const decodedPath = decodeURIComponent(path);
    const pathParts = decodedPath.split(' / ').map((part) => part.trim());
    return pathParts.map(escapePathPart).join('.');
  } catch {
    const pathParts = path.split(' / ').map((part) => part.trim());
    return pathParts.map(escapePathPart).join('.');
  }
};

/**
 * FQN을 경로 문자열로 변환
 * @param fqn - OpenMetadata FQN
 * @returns 경로 문자열 (따옴표 제거됨)
 *
 * @example
 * convertFqnToPath('ceph-s3."lges.eswa.lam3"')
 * // returns: 'ceph-s3 / lges.eswa.lam3'
 *
 * @example
 * convertFqnToPath('bronze-iceberg.bronze.phase0_keti_test.test_bulk')
 * // returns: 'bronze-iceberg / bronze / phase0_keti_test / test_bulk'
 */
export const convertFqnToPath = (fqn: string): string => {
  try {
    const pathParts = parseFqnToPathParts(fqn, false);
    return pathParts.join(' / ');
  } catch {
    return fqn;
  }
};

/**
 * FQN을 경로 배열로 변환 (역변환)
 * 따옴표로 감싸진 부분을 올바르게 파싱하되, 따옴표는 제거
 *
 * @param fqn - OpenMetadata FQN
 * @param keepQuotes - true면 따옴표 유지, false면 제거 (기본값: false)
 * @returns 경로 부분 배열
 *
 * @example
 * parseFqnToPathParts('ceph-s3."lges.eswa.lam3"')
 * // returns: ['ceph-s3', 'lges.eswa.lam3']
 *
 * @example
 * parseFqnToPathParts('glossary."g.pdx-v2"."설비 계층 구조".Area', true)
 * // returns: ['glossary', '"g.pdx-v2"', '"설비 계층 구조"', 'Area']
 */
export const parseFqnToPathParts = (fqn: string, keepQuotes: boolean = false): string[] => {
  if (!fqn) return [];

  const parts: string[] = [];
  let currentPart = '';
  let inQuotes = false;

  for (let i = 0; i < fqn.length; i++) {
    const char = fqn[i];

    if (char === '"') {
      inQuotes = !inQuotes;
      if (keepQuotes) {
        currentPart += char;
      }
    } else if (char === '.' && !inQuotes) {
      if (currentPart) {
        parts.push(currentPart);
        currentPart = '';
      }
    } else {
      currentPart += char;
    }
  }

  if (currentPart) {
    parts.push(currentPart);
  }

  return parts;
};

/**
 * FQN 앞뒤의 불필요한 큰따옴표만 제거 (내부 따옴표는 유지)
 *
 * @description
 * 일부 검색 결과에서 FQN 전체가 큰따옴표로 감싸져 있는 경우가 있습니다.
 * 이 함수는 FQN 앞뒤의 큰따옴표만 제거하고, FQN 내부의 특수문자를 감싼 따옴표는 유지합니다.
 *
 * @param fqn - OpenMetadata FQN (앞뒤에 큰따옴표가 있을 수 있음)
 * @returns 앞뒤 따옴표가 제거된 FQN
 *
 * @example
 * ensureFqnQuotes('"ingestion-metadata.kafka-connect-simulator.config.\\"system.sessions\\""')
 * // returns: 'ingestion-metadata.kafka-connect-simulator.config."system.sessions"'
 *
 * @example
 * ensureFqnQuotes('ingestion-metadata.kafka-connect-simulator.config."system.sessions"')
 * // returns: 'ingestion-metadata.kafka-connect-simulator.config."system.sessions"' (변경 없음)
 *
 * @example
 * ensureFqnQuotes('mysql.production.public.users')
 * // returns: 'mysql.production.public.users' (변경 없음)
 */
export const ensureFqnQuotes = (fqn: string): string => {
  if (!fqn) return '';

  let cleanedFqn = fqn.trim();

  if (cleanedFqn.startsWith('"') && cleanedFqn.endsWith('"')) {
    const closingQuoteIndex = cleanedFqn.indexOf('"', 1);

    if (closingQuoteIndex === cleanedFqn.length - 1) {
      cleanedFqn = cleanedFqn.slice(1, -1);
    }
  }

  return cleanedFqn;
};

/**
 * FQN 앞뒤의 잘못된 큰따옴표를 제거하고 OpenMetadata API 호출용으로 인코딩
 *
 * @description
 * 일부 검색 결과에서 FQN 전체가 큰따옴표로 감싸져 있는 경우가 있습니다.
 * 이 함수는 앞뒤 큰따옴표를 제거한 후 올바르게 인코딩합니다.
 *
 * @param fqn - OpenMetadata FQN (앞뒤에 큰따옴표가 있을 수 있음)
 * @returns OpenMetadata API 호출에 사용 가능한 인코딩된 FQN
 *
 * @example
 * encodeOpenMetadataFqnSafe('"bronze-iceberg.bronze.videos_binary_test.videos_binary"')
 * // returns: 'bronze-iceberg.bronze.videos_binary_test.videos_binary'
 *
 * @example
 * encodeOpenMetadataFqnSafe('glossary."g.pdx-v2"."설비 계층 구조".Area')
 * // returns: 'glossary.%22g.pdx-v2%22.설비%20계층%20구조.Area'
 */
export const encodeOpenMetadataFqnSafe = (fqn: string): string => {
  if (!fqn) {
    return '';
  }

  let cleanedFqn = fqn.trim();

  if (cleanedFqn.startsWith('"') && cleanedFqn.endsWith('"') && cleanedFqn.length > 2) {
    cleanedFqn = cleanedFqn.slice(1, -1);
  }

  return encodeOpenMetadataFqn(cleanedFqn);
};

/**
 * FQN을 OpenMetadata API 호출용으로 인코딩
 * OpenMetadata의 인코딩 규칙:
 * - 점이 포함된 부분: 따옴표 유지하고 전체를 인코딩 ("g.pdx-v2" → %22g.pdx-v2%22)
 * - 공백이 포함된 부분: 따옴표 제거하고 공백만 인코딩 ("설비 계층 구조" → 설비%20계층%20구조)
 *
 * @param fqn - OpenMetadata FQN
 * @returns OpenMetadata API용 인코딩된 FQN
 *
 * @example
 * encodeOpenMetadataFqn('glossary."g.pdx-v2"."설비 계층 구조".Area')
 * // returns: 'glossary.%22g.pdx-v2%22.설비%20계층%20구조.Area'
 */
export const encodeOpenMetadataFqn = (fqn: string): string => {
  if (!fqn) return '';

  const parts: string[] = [];
  let currentPart = '';
  let inQuotes = false;
  let quotedContent = '';

  for (let i = 0; i < fqn.length; i++) {
    const char = fqn[i];

    if (char === '"') {
      if (!inQuotes) {
        inQuotes = true;
        quotedContent = '';
      } else {
        inQuotes = false;
        const hasDot = quotedContent.includes('.');
        if (hasDot) {
          parts.push(encodeURIComponent(`"${quotedContent}"`));
        } else {
          parts.push(quotedContent.replace(/ /g, '%20'));
        }
        quotedContent = '';
      }
    } else if (char === '.' && !inQuotes) {
      if (currentPart) {
        const needsEncoding = !/^[a-zA-Z0-9\-_]+$/.test(currentPart);
        parts.push(needsEncoding ? encodeURIComponent(currentPart) : currentPart);
        currentPart = '';
      }
      parts.push('.');
    } else {
      if (inQuotes) {
        quotedContent += char;
      } else {
        currentPart += char;
      }
    }
  }

  if (currentPart) {
    const needsEncoding = !/^[a-zA-Z0-9\-_]+$/.test(currentPart);
    parts.push(needsEncoding ? encodeURIComponent(currentPart) : currentPart);
  }

  return parts.join('');
};

/**
 * FQN을 URL 안전한 형식으로 인코딩
 * @param fqn - OpenMetadata FQN
 * @returns URL 인코딩된 FQN
 *
 * @example
 * encodeFqnForUrl('glossary."g.pdx-v2"."설비 계층 구조".Area')
 * // returns: 'glossary.%22g.pdx-v2%22.%22%EC%84%A4%EB%B9%84%20%EA%B3%84%EC%B8%B5%20%EA%B5%AC%EC%A1%B0%22.Area'
 */
export const encodeFqnForUrl = (fqn: string): string => {
  return encodeURIComponent(fqn);
};

/**
 * URL에서 FQN 디코딩
 * @param encodedFqn - URL 인코딩된 FQN
 * @returns 디코딩된 FQN
 */
export const decodeFqnFromUrl = (encodedFqn: string): string => {
  try {
    return decodeURIComponent(encodedFqn);
  } catch {
    return encodedFqn;
  }
};

/**
 * FQN 파트 정보
 */
export type FqnPart = {
  value: string;
  entityType: string;
  fqn: string;
};

/**
 * OpenMetadata 자산 타입별 계층 구조 정의
 * service는 모든 자산의 최상위 계층
 */
const ASSET_HIERARCHY_MAP: Record<string, string[]> = {
  table: ['service', 'database', 'databaseSchema', 'table'],
  databaseSchema: ['service', 'database', 'databaseSchema'],
  database: ['service', 'database'],
  topic: ['service', 'topic'],
  dashboard: ['service', 'dashboard'],
  pipeline: ['service', 'pipeline'],
  mlmodel: ['service', 'mlmodel'],
  container: ['service', 'container'],
  apiEndpoint: ['service', 'apiCollection', 'apiEndpoint'],
  apiCollection: ['service', 'apiCollection'],
  searchIndex: ['service', 'searchIndex'],
} as const;

/**
 * FQN을 파싱하여 각 부분의 타입 정보와 함께 반환
 * OpenMetadata의 계층 구조를 기반으로 각 FQN 부분의 entityType을 추론
 *
 * @param fqn - OpenMetadata FQN
 * @param entityType - 현재 자산의 타입 (table, database, topic 등)
 * @returns FQN 파트 배열 (각 파트는 value, entityType, fqn 포함)
 *
 * @example
 * parseFqnWithTypes('lges-metadata.LGES_metadata.public.users', 'table')
 * // returns: [
 * //   { value: 'lges-metadata', entityType: 'service', fqn: 'lges-metadata' },
 * //   { value: 'LGES_metadata', entityType: 'database', fqn: 'lges-metadata.LGES_metadata' },
 * //   { value: 'public', entityType: 'databaseSchema', fqn: 'lges-metadata.LGES_metadata.public' },
 * //   { value: 'users', entityType: 'table', fqn: 'lges-metadata.LGES_metadata.public.users' }
 * // ]
 *
 * @example
 * parseFqnWithTypes('kafka.user-events', 'topic')
 * // returns: [
 * //   { value: 'kafka', entityType: 'service', fqn: 'kafka' },
 * //   { value: 'user-events', entityType: 'topic', fqn: 'kafka.user-events' }
 * // ]
 */
export const parseFqnWithTypes = (fqn: string, entityType: string): FqnPart[] => {
  if (!fqn) return [];

  const parts = parseFqnToPathParts(fqn);
  const hierarchy = ASSET_HIERARCHY_MAP[entityType] || ['service', entityType];

  const result: FqnPart[] = [];
  let accumulatedFqn = '';

  for (let i = 0; i < parts.length; i++) {
    accumulatedFqn = accumulatedFqn ? `${accumulatedFqn}.${parts[i]}` : parts[i];

    result.push({
      value: parts[i],
      entityType: hierarchy[i] || entityType,
      fqn: accumulatedFqn,
    });
  }

  return result;
};

/**
 * FQN에서 따옴표를 제거
 *
 * @description
 * OpenMetadata FQN의 따옴표(")를 제거합니다.
 * URL 생성 시나 화면 표시 시 따옴표 없이 사용할 때 활용합니다.
 *
 * @param fqn - 따옴표가 포함될 수 있는 FQN 문자열
 * @returns 따옴표가 제거된 FQN 문자열
 *
 * @example
 * removeFqnQuotes('ceph-s3."lges.eswa.lam3"')
 * // returns: 'ceph-s3.lges.eswa.lam3'
 *
 * @example
 * removeFqnQuotes('glossary."g.pdx-v2"."설비 계층 구조".Area')
 * // returns: 'glossary.g.pdx-v2.설비 계층 구조.Area'
 *
 * @example
 * removeFqnQuotes('mysql.production.public.users')
 * // returns: 'mysql.production.public.users'
 */
export const removeFqnQuotes = (fqn: string): string => {
  return fqn.replace(/"/g, '');
};

/**
 * FQN의 깊이(depth)를 계산
 *
 * @description
 * FQN에서 점(.)으로 구분된 계층의 깊이를 계산합니다.
 * 깊이는 0부터 시작하며, 점의 개수로 계산됩니다.
 *
 * @param fqn - OpenMetadata FQN
 * @returns FQN의 깊이 (0부터 시작)
 *
 * @example
 * calculateFqnDepth('data_asset')
 * // returns: 0
 *
 * @example
 * calculateFqnDepth('data_asset.데이터베이스')
 * // returns: 1
 *
 * @example
 * calculateFqnDepth('data_asset.데이터베이스.iceberg')
 * // returns: 2
 *
 * @example
 * calculateFqnDepth('data_asset.데이터베이스.iceberg.child')
 * // returns: 3
 */
export const calculateFqnDepth = (fqn: string): number => {
  if (!fqn) return 0;
  return fqn.split('.').length - 1;
};

/**
 * FQN에서 카테고리 prefix를 제거
 *
 * @description
 * 용어 FQN에서 첫 번째 부분(카테고리)을 제거합니다.
 * 예: 'data_asset.데이터베이스.iceberg' → '데이터베이스.iceberg'
 *
 * @param fqn - OpenMetadata FQN
 * @returns 카테고리가 제거된 FQN
 *
 * @example
 * removeCategoryPrefix('data_asset.데이터베이스.iceberg')
 * // returns: '데이터베이스.iceberg'
 *
 * @example
 * removeCategoryPrefix('data_asset.데이터베이스')
 * // returns: '데이터베이스'
 *
 * @example
 * removeCategoryPrefix('data_asset')
 * // returns: 'data_asset' (카테고리만 있으면 그대로 반환)
 */
export const removeCategoryPrefix = (fqn: string): string => {
  if (!fqn) return fqn;
  const parts = parseFqnToPathParts(fqn);
  if (parts.length <= 1) return fqn;
  return parts.slice(1).join('.');
};

/**
 * FQN에서 부모 FQN을 추출
 *
 * @description
 * FQN에서 마지막 부분을 제거하여 부모 FQN을 반환합니다.
 * 부모가 없으면 빈 문자열을 반환합니다.
 *
 * @param fqn - OpenMetadata FQN
 * @param fallbackToRoot - true면 부모가 없을 때 루트(첫 번째 요소)를 반환
 * @returns 부모 FQN (부모가 없으면 빈 문자열 또는 루트)
 *
 * @example
 * getParentFqn('data_asset.데이터베이스.iceberg')
 * // returns: 'data_asset.데이터베이스'
 *
 * @example
 * getParentFqn('data_asset.데이터베이스')
 * // returns: 'data_asset'
 *
 * @example
 * getParentFqn('data_asset')
 * // returns: '' (부모 없음)
 *
 * @example
 * getParentFqn('data_asset', true)
 * // returns: 'data_asset' (루트 반환)
 */
export const getParentFqn = (fqn: string | undefined | null, fallbackToRoot = false): string => {
  if (!fqn) return '';
  const parts = parseFqnToPathParts(fqn, true);
  if (parts.length <= 1) {
    const firstPart = parts[0] || '';
    return fallbackToRoot ? firstPart.replace(/^"|"$/g, '') : '';
  }
  return parts.slice(0, -1).join('.');
};

/**
 * 서비스명에서 prefix 추출
 *
 * @description
 * 서비스명(FQN의 첫 번째 부분)에서 하이픈(-) 앞의 prefix를 추출합니다.
 * 예: 'bronze-iceberg' → 'bronze', 'silver-s3' → 'silver'
 *
 * @param serviceName - 서비스명
 * @returns prefix (bronze/silver/gold) 또는 null
 *
 * @example
 * extractServiceNamePrefix('bronze-iceberg')
 * // returns: 'bronze'
 *
 * @example
 * extractServiceNamePrefix('silver-s3')
 * // returns: 'silver'
 *
 * @example
 * extractServiceNamePrefix('gold-postgres')
 * // returns: 'gold'
 *
 * @example
 * extractServiceNamePrefix('mysql')
 * // returns: null (하이픈 없음)
 */
export const extractServiceNamePrefix = (serviceName: string): string | null => {
  if (!serviceName) return null;

  const hyphenIndex = serviceName.indexOf('-');
  if (hyphenIndex === -1) return null;

  const prefix = serviceName.substring(0, hyphenIndex).toLowerCase();

  if (prefix === 'bronze' || prefix === 'silver' || prefix === 'gold') {
    return prefix;
  }

  return null;
};

/**
 * FQN에서 certification 정보 추출
 *
 * @description
 * FQN의 서비스명(첫 번째 부분)에서 bronze/silver/gold prefix를 추출하여
 * certification 정보를 반환합니다.
 *
 * @param fqn - OpenMetadata FQN
 * @returns certification 정보 ({ name: string, color: string }) 또는 null
 *
 * @example
 * getCertificationFromFqn('bronze-iceberg.bronze.ax_nongshim.nongshim_current_test')
 * // returns: { name: 'Bronze', color: '#color-code' }
 *
 * @example
 * getCertificationFromFqn('silver-s3.my-bucket')
 * // returns: { name: 'Silver', color: '#color-code' }
 *
 * @example
 * getCertificationFromFqn('mysql.production.public.users')
 * // returns: null (prefix 없음)
 */
export const getCertificationFromFqn = (
  fqn: string,
  certificationColors: Record<string, string>
): { name: string; color: string } | null => {
  if (!fqn) return null;

  const parts = parseFqnToPathParts(fqn);
  if (parts.length === 0) return null;

  const serviceName = parts[0];
  const prefix = extractServiceNamePrefix(serviceName);

  if (!prefix) return null;

  const certificationName = prefix.charAt(0).toUpperCase() + prefix.slice(1);
  const color = certificationColors[certificationName] || '#8c8c8c';

  return { name: certificationName, color };
};
